import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/services/axios'
import { useToast } from 'primevue/usetoast'
import type {DriverStats, Order} from '@/types'


export const useDriverStore = defineStore('driver', () => {
  // State
  const stats = ref<DriverStats>({
    isAvailable: false,
    totalOrders: 0,
    completedOrders: 0,
  })

  const ordersPagination = ref({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0,
  })
  const recentOrders = ref<Order[]>([])
  const loading = ref(false)
  const error = ref(null)

  // Actions
  async function fetchDashboardData(dateFilter = 'today', startDate = null, endDate = null) {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/api/driver/dashboard?date_filter=${dateFilter}&start_date=${startDate}&end_date=${endDate}`)

      // Update stats and current orders from the dashboard endpoint
      stats.value = response.data.stats
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load dashboard data'
      error.value = errorMessage
      console.error('Error fetching dashboard data:', err)
    } finally {
      loading.value = false
    }
  }

  async function fetchRecentOrders(page = 1) {
    loading.value = true
    error.value = null
    try {
      const response = await api.get(`/api/driver/recent-orders?page=${page}`)
      recentOrders.value = response.data.recentOrders
      ordersPagination.value = response.data.pagination
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to load recent orders'
      console.error('Error fetching recent orders:', err)
    } finally {
      loading.value = false
    }
  }

  async function toggleAvailability() {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/api/driver/toggle-availability')
      stats.value.isAvailable = response.data.isAvailable
      return response.data.isAvailable
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update availability'
      console.error('Error toggling availability:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateOrderStatus(orderId: number, status: string) {
    try {
      const response = await api.post(`/api/driver/orders/${orderId}/status`, { status })

      // Update the order in the current orders list
      const index = recentOrders.value.findIndex(order => order.id === orderId)
      if (index !== -1) {
        recentOrders.value[index] = response.data
      }

      // If order is completed, refresh all dashboard data
      if (status === 'delivered') {
        await fetchDashboardData()
      }

      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update order status'
      console.error('Error updating order status:', err)
      throw err
    }
  }

  // Initialize data
  function initialize() {
    fetchDashboardData()
    fetchRecentOrders()
  }

  return {
    stats,
    recentOrders,
    ordersPagination,
    loading,
    error,
    fetchDashboardData,
    toggleAvailability,
    updateOrderStatus,
    initialize,
    fetchRecentOrders
  }
})
